import { Tabs, TabsContent, TabsList, TabsTrigger } from "@workspace/web-ui/components/tabs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import {
  patientAppointmentsQueryOptions,
  patientChatHistoryQueryOptions,
  patientDetailsQueryOptions,
  patientDiagnosisQueryOptions,
  // patientDiagnosisQueryOptions,
  // patientDiagnosisQueryOptions,
  // patientMedicalHistoryQueryOptions,
  patientMedicalPlanQueryOptions,
  // patientMedicationQueryOptions,
  patientSessionQueryOption,
  urgentPatientMedicationQueryOptions,
} from "../../api/queries";
import {
  TimelineSkeleton,
  MedicationSkeleton,
  ChatHistorySkeleton,
} from "../skeletons/tab-content-skeletons";
import { useState } from "react";
import { DiagnosisOverview } from "../tab-content/diagnosis";
import { ChatHistory } from "../tab-content/chat-history";
import { Appointment } from "../tab-content/appointment";

import { PatientSessions, Patient } from "../../api/types";
import UrgentCareMedication from "../tab-content/urgent-care-medication";

type SessionType = PatientSessions['sessions'][0];

type PatientLogTabsProps = {
  userId: string;
  selectedSession?: SessionType;
  program: string;
  patient: Patient;
  userDoseSpotId?: string;
};

export const TAB_CONFIG = [
  { value: "timeline", label: "Diagnosis" },
  { value: "medication", label: "Medication" },
  { value: "appointment", label: "Appointments" },
  { value: "chat-history", label: "Chat History" },
] as const;

export type TabValue = typeof TAB_CONFIG[number]["value"];

export const UrgentCareDetailsTabs = ({ userId, selectedSession, program, patient, userDoseSpotId }: PatientLogTabsProps) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("timeline");

  // Get sessionId from route search params
  const { sessionId } = useSearch({ strict: false }) as { sessionId?: string };

  const { data: patientData, isLoading: isPatientLoading } = useQuery(
    patientDetailsQueryOptions(userId, program)
  );

  const { data: diagnosisData, isLoading: isTimelineLoading } = useQuery(
    patientSessionQueryOption(userId, program)
  );

  // Only fetch detailed diagnosis data if there's a specific sessionId from route
  // If we have selectedSession, we should use the data from diagnosisData instead
  const { data: patientDiagnosisData, isLoading: isPatientDiagnosisLoading } = useQuery({
    ...patientDiagnosisQueryOptions(userId, sessionId ?? "", program),
    enabled: !!sessionId // Only run query if we have a sessionId from route
  });

  // const { data: appointmentData, isLoading: isAppointmentLoading } = useQuery(
  //   patientAppointmentsQueryOptions(userId, selectedSession?.session_id ?? "", program)
  // );

  const { data: chatHistoryData, isLoading: isChatHistoryLoading } = useQuery(
    patientChatHistoryQueryOptions(userId, selectedSession?.session_id ?? "", program)
  );

  const { data: urgentMedicationData, isLoading: isUrgentMedicationLoading } = useQuery(
    urgentPatientMedicationQueryOptions(userId, selectedSession?.session_id ?? "", program, "prescribed")
  );

  const { data: urgentCurrentMedicationData, isLoading: isCurrentUrgentMedicationLoading } = useQuery(
    urgentPatientMedicationQueryOptions(userId, selectedSession?.session_id ?? "", program, "active")
  );

  const { data: urgentRequestsMedicationData, isLoading: isRequestsUrgentMedicationLoading } = useQuery(
    urgentPatientMedicationQueryOptions(userId, selectedSession?.session_id ?? "", program, "requests")
  );

  const { data: urgentHistoryMedicationData, isLoading: isHistoryUrgentMedicationLoading } = useQuery(
    urgentPatientMedicationQueryOptions(userId, selectedSession?.session_id ?? "", program, "historic")
  );
  
  const { data: medicalPlanData, isLoading: isMedicalPlanLoading } = useQuery(
    patientMedicalPlanQueryOptions(userId, program)
  );

  // const { data: medicalHistoryData, isLoading: isMedicalHistoryLoading } = useQuery(
  //   patientMedicalHistoryQueryOptions(userId, program)
  // );

  // const { data: diagnosisBData, isLoading: isDagnosisBLoading } = useQuery(
  //   patientDiagnosisQueryOptions(userId, program)
  // );



  const prefetchTabData = (tabValue: string): void => {
    if (!userId) return;

    switch (tabValue) {
      case "timeline":
        queryClient.prefetchQuery(patientSessionQueryOption(userId, program));
        break;
      case "medication":
        // queryClient.prefetchQuery(patientMedicationQueryOptions(userId, program));
        queryClient.prefetchQuery(urgentPatientMedicationQueryOptions(userId, selectedSession?.session_id ?? "", program, "prescribed"));
        queryClient.prefetchQuery(urgentPatientMedicationQueryOptions(userId, selectedSession?.session_id ?? "", program, "active"));
        queryClient.prefetchQuery(urgentPatientMedicationQueryOptions(userId, selectedSession?.session_id ?? "", program, "requests"));
        queryClient.prefetchQuery(urgentPatientMedicationQueryOptions(userId, selectedSession?.session_id ?? "", program, "historic"));
        break;
      case "appointment":
        if (selectedSession?.session_id) {
          queryClient.prefetchQuery(patientAppointmentsQueryOptions(userId, selectedSession.session_id, program));
        }
        break;
      case "chat-history":
        if (selectedSession?.session_id) {
          queryClient.prefetchQuery(patientChatHistoryQueryOptions(userId, selectedSession.session_id, program));
        }
        break;
    }
  };

  const handleTabChange = (value: string): void => {
    setActiveTab(value);
  };

  return (
    <Tabs defaultValue="timeline" value={activeTab} className="w-full" onValueChange={handleTabChange}>
      <div className="w-full flex overflow-x-auto pb-1 sm:pb-2">
        <TabsList className="flex border-b no-scrollbar bg-transparent px-0 py-0 sm:py-1 whitespace-nowrap">
          {TAB_CONFIG.map(({ value, label }) => (
            <TabsTrigger
              key={value}
              value={value}
              className={`
                tab-trigger flex-shrink-0 text-xs sm:text-sm md:text-base whitespace-nowrap py-2
                ${activeTab === value ? "font-medium" : "font-normal"}
              `}
              onMouseEnter={() => prefetchTabData(value)}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
      <div className="w-full mt-4">
        <TabsContent value="timeline">
          {(isTimelineLoading || (sessionId && isPatientDiagnosisLoading)) ? (
            <TimelineSkeleton />
          ) : (
            <DiagnosisOverview
              diagnosisData={sessionId ? patientDiagnosisData : diagnosisData}
              userId={userId}
              userDoseSpotId={userDoseSpotId}
              selectedSession={selectedSession}
              patient={patient}
            />
          )}
        </TabsContent>
        <TabsContent value="medication">
          {isUrgentMedicationLoading || isCurrentUrgentMedicationLoading || isRequestsUrgentMedicationLoading || isHistoryUrgentMedicationLoading || isPatientLoading || isMedicalPlanLoading ? (
            <MedicationSkeleton />
          ) : (
            patientData && medicalPlanData && (
              <UrgentCareMedication
                medicalPlanData={medicalPlanData}
                urgentMedicationData={urgentMedicationData}
                urgentCurrentMedicationData={urgentCurrentMedicationData}
                urgentRequestsMedicationData={urgentRequestsMedicationData}
                urgentHistoryMedicationData={urgentHistoryMedicationData}
                patient={medicalPlanData}
                userId={patientData.userId}
                selectedSession={selectedSession}
                patientData={patientData}
              />
            )
          )}
        </TabsContent>
        <TabsContent value="appointment">
          <Appointment selectedSession={selectedSession} userId={userId} />
        </TabsContent>
        <TabsContent value="chat-history">
          {isChatHistoryLoading ? (
            <ChatHistorySkeleton />
          ) : (
            <ChatHistory selectedSession={selectedSession} chatHistory={chatHistoryData} />
          )}
        </TabsContent>
      </div>
    </Tabs>
  );
};
